import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  ListItemIcon,
} from "@mui/material";
import {
  IconRobot,
  IconMessage,
  IconHeartHandshake,
  IconFilePlus,
  IconSignature,
  IconArticle,
  IconKey,
  IconMoon,
  IconBrightnessUp,
  IconLogout,
} from "@tabler/icons-react";
import headerImage from "../../assets/image/logos/lawBlocks-full-logo-white.svg";
import Darkheader from "../../assets/image/logos/lawBlocks-full-logo-black.svg";
import paths from "../../utils/paths";

export default function MobileDrawer({
  setMobileMenuOpen,
  mobileMenuOpen,
  mode,
  handleLightMode,
  handleDarkMode,
  isLightMode,
  handleLogout,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingPath, setPendingPath] = useState(null);

  const redirect = (path) => {
    setPendingPath(path);
    setMobileMenuOpen(false);
  };

  const menuItems = [
    {
      text: "Create AI document",
      path: paths.CreateDocumentWithAi,
      icon: IconRobot,
    },
    {
      text: "AI Chat",
      path: paths.Chat,
      icon: IconMessage,
    },
    {
      text: "Arbitration & Mediation",
      path: paths.Arbitrators,
      icon: IconHeartHandshake,
    },
    {
      text: "Upload Contract",
      path: paths.CreateTemplate,
      icon: IconFilePlus,
    },
    {
      text: "Digital Sign",
      path: paths.DigitalDashboard,
      icon: IconSignature,
    },
    {
      text: "Legal Document",
      path: paths.LegalDocuments,
      icon: IconArticle,
    },
    {
      text: "API Key Dashboard",
      path: paths.ApiKeyDashboard,
      icon: IconKey,
    },
  ];

  return (
    <>
      {/* MOBILE MENU DRAWER */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        sx={{
          "& .MuiDrawer-paper": {
            width: "250px",
            boxSizing: "border-box",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: mode === "light" ? "#ffffff" : "#1a1b1e",
          },
        }}
        onClose={() => setMobileMenuOpen(false)}
        SlideProps={{
          onExited: () => {
            if (pendingPath) {
              navigate(pendingPath);
              setPendingPath(null);
            }
          },
        }}
      >
        {/* Top Logo Section */}
        <Box
          sx={{
            py: 2,
            px: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70px",
            flexShrink: 0,
          }}
        >
          <NavLink to="/" onClick={() => setMobileMenuOpen(false)}>
            <img
              src={mode === "light" ? Darkheader : headerImage}
              alt="Header"
              style={{ width: "140px", display: "block" }}
            />
          </NavLink>
        </Box>

        <Divider sx={{ borderColor: mode === "light" ? "#989ca1ff" : "#797981ff", flexShrink: 0 }} />

        {/* Middle Scrollable Section */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" ? "#cbd5e1" : "#4b5563",
              borderRadius: "4px",
            },
          }}
        >
          <List sx={{ width: "100%", py: 1, px: 1 }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <ListItemButton
                  key={item.text}
                  onClick={() => redirect(item.path)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    color: isActive
                      ? mode === "light"
                        ? "#0f172a"
                        : "#f1f5f9"
                      : mode === "light"
                      ? "#475569"
                      : "#94a3b8",
                    backgroundColor: isActive
                      ? mode === "light"
                        ? "#f1f5f9"
                        : "#25262b"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: mode === "light" ? "#f8fafc" : "#25262b",
                      color: mode === "light" ? "#0f172a" : "#f1f5f9",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 35,
                      color: isActive
                        ? mode === "light"
                          ? "#0f172a"
                          : "#f1f5f9"
                        : mode === "light"
                        ? "#64748b"
                        : "#64748b",
                    }}
                  >
                    <Icon size={20} stroke={2} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Divider sx={{ borderColor: mode === "light" ? "#989ca1ff" : "#797981ff", flexShrink: 0 }} />

        {/* Bottom Section */}
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <List sx={{ p: 0 }}>
            {/* Theme Toggle Button */}
            <ListItemButton
              onClick={() => {
                isLightMode ? handleDarkMode() : handleLightMode();
              }}
              sx={{
                borderRadius: "8px",
                mb: 0.5,
                py: 1,
                px: 2,
                color: mode === "light" ? "#475569" : "#94a3b8",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#f8fafc" : "#25262b",
                  color: mode === "light" ? "#0f172a" : "#f1f5f9",
                },
                transition: "all 0.2s",
              }}
            >
              <ListItemIcon sx={{ minWidth: 35, color: mode === "light" ? "#64748b" : "#64748b" }}>
                {isLightMode ? <IconMoon size={20} stroke={2} /> : <IconBrightnessUp size={20} stroke={2} />}
              </ListItemIcon>
              <ListItemText
                primary={`${isLightMode ? "Dark" : "Light"} Mode`}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            </ListItemButton>

            {/* Logout Button */}
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: "8px",
                py: 1,
                px: 2,
                color: "#ef4444",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#fef2f2" : "#3c1818",
                },
                transition: "all 0.2s",
              }}
            >
              <ListItemIcon sx={{ minWidth: 35, color: "#ef4444" }}>
                <IconLogout size={20} stroke={2} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
